import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const NotifContext = createContext(null);

// ── Helpers ────────────────────────────────────────────────────────────────

const LS_KEY = (uid) => `onet_notifs_${uid}`;

const PRIORITY = {
    past_due:        0,
    deadline:        1,
    request_pending: 2,
    cte_propagated:  3,   // CTE da manager
    message:         4,
    broadcast:       5,
    new_sub:         6,
    payment_ok:      7,
    new_contract:    8,
};

/** Ordina: non lette > urgenza tipo > timestamp desc */
const sortNotifs = (arr) => [...arr].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    const pa = PRIORITY[a.type] ?? 99;
    const pb = PRIORITY[b.type] ?? 99;
    if (pa !== pb) return pa - pb;
    return new Date(b.time) - new Date(a.time);
});

const saveToLS = (uid, notifs) => {
    try {
        localStorage.setItem(LS_KEY(uid), JSON.stringify({
            ts: Date.now(),
            data: notifs.slice(0, 80),
        }));
    } catch (_) {}
};

const loadFromLS = (uid) => {
    try {
        const raw = localStorage.getItem(LS_KEY(uid));
        if (!raw) return [];
        const { ts, data } = JSON.parse(raw);
        if (Date.now() - ts > 24 * 3600 * 1000) return []; // TTL 24h
        return data || [];
    } catch (_) { return []; }
};

/**
 * NotificationProvider
 *
 * Feature:
 *  - 6 sorgenti dati (monitoring, broadcasts, deadlines, contracts, requests, messages)
 *  - Persistenza lettura su `notification_reads` (Supabase)
 *  - localStorage cache per zero-flicker al refresh
 *  - Scadenze overdue (past due_date, non completate) come alert urgenti
 *  - Priority sort (non lette > urgenza > timestamp)
 *  - Preferenze utente da `notification_preferences`
 *  - `preferences` esposto per useBrowserNotifications
 */
export function NotificationProvider({ children }) {
    const { effectiveProfile: profile, user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [preferences, setPreferences]     = useState(null);
    const channelRef    = useRef(null);
    const markReadTimer = useRef(null);
    const readKeysRef   = useRef(new Set());   // cache locale delle chiavi già lette

    const orgId    = profile?.organization_id;
    const userId   = profile?.id;
    const authId   = user?.id;
    const isAgency = ['agency', 'admin'].includes(profile?.role);
    const role     = profile?.role;

    // unread calcolato in modo derivato
    const unread = notifications.filter(n => !n.read).length;

    // ── Helpers interni ────────────────────────────────────────────────────

    const isTypeEnabled = useCallback((type) => {
        if (!preferences?.enabled_types) return true;
        return preferences.enabled_types.includes(type);
    }, [preferences]);

    const addNotif = useCallback((notif) => {
        if (!isTypeEnabled(notif.type)) return;
        const isRead = readKeysRef.current.has(notif.id);
        const finalNotif = { ...notif, read: isRead || notif.read };
        setNotifications(prev => {
            if (prev.some(n => n.id === finalNotif.id)) return prev;
            return sortNotifs([finalNotif, ...prev].slice(0, 80));
        });
    }, [isTypeEnabled]);

    // ── Carica preferenze utente ───────────────────────────────────────────
    const loadPreferences = useCallback(async () => {
        if (!authId) return;
        const { data } = await supabase
            .from('notification_preferences')
            .select('*')
            .eq('user_id', authId)
            .maybeSingle();
        if (data) {
            setPreferences(data);
        } else {
            // Crea riga di default
            const { data: created } = await supabase
                .from('notification_preferences')
                .insert({ user_id: authId })
                .select()
                .single();
            if (created) setPreferences(created);
        }
    }, [authId]);

    // ── Carica notification_reads per segnare le lette ─────────────────────
    const loadReadKeys = useCallback(async () => {
        if (!authId) return;
        const { data } = await supabase
            .from('notification_reads')
            .select('notification_key')
            .eq('user_id', authId);
        if (data) {
            readKeysRef.current = new Set(data.map(r => r.notification_key));
        }
    }, [authId]);

    // ── Persiste una chiave come letta su DB ───────────────────────────────
    const persistRead = useCallback((key) => {
        if (!authId || readKeysRef.current.has(key)) return;
        readKeysRef.current.add(key);
        supabase.from('notification_reads')
            .insert({ user_id: authId, notification_key: key })
            .then(() => {}).catch(() => {});
    }, [authId]);

    // ── Fetch aggregato via RPC (1 roundtrip invece di 7) ────────────────────
    const fetchAllNotifications = useCallback(async () => {
        if (!authId) return;
        try {
            console.log('[NotifCtx] RPC fetch notifications...');
            const { data, error } = await supabase.rpc('get_dashboard_notifications', {
                p_auth_id: authId,
                p_org_id:  orgId  || null,
                p_role:    role   || null,
            });
            if (error) { console.error('[NotifCtx] RPC error:', error.message); return; }
            if (!data)  return;

            const TYPE_COLOR = { warning: '#f59e0b', promo: '#a78bfa', info: '#38bdf8' };
            const today = new Date(); today.setHours(0, 0, 0, 0);

            // 1. Notifiche in-app (CTE propagate, system)
            (data.in_app || []).forEach(n => {
                if (!isTypeEnabled(n.type)) return;
                const isRead = readKeysRef.current.has(`inapp-${n.id}`) || n.read;
                addNotif({
                    id:       `inapp-${n.id}`,
                    dbId:     n.id,
                    type:     n.type || 'broadcast',
                    title:    n.title,
                    message:  n.message,
                    time:     n.created_at,
                    color:    '#22d3ee',
                    read:     isRead,
                    link:     n.link || '/app/notifications',
                    metadata: n.metadata || {},
                });
            });

            // 2. Broadcasts
            if (isTypeEnabled('broadcast')) {
                (data.broadcasts || []).forEach(b => {
                    const isRead = readKeysRef.current.has(`broadcast-${b.id}`);
                    addNotif({
                        id: `broadcast-${b.id}`, broadcastId: b.id,
                        type: 'broadcast', title: b.subject, message: b.message,
                        time: b.sent_at, color: TYPE_COLOR[b.type] || '#38bdf8',
                        read: isRead,
                    });
                });
            }

            // 3. Messaggi manager
            if (isTypeEnabled('message')) {
                (data.messages || []).forEach(m => {
                    addNotif({
                        id: `msg-${m.id}`, type: 'message',
                        title: `💬 ${m.subject || 'Nuovo messaggio'}`,
                        message: (m.message || '').slice(0, 100) + ((m.message?.length || 0) > 100 ? '…' : ''),
                        time: m.created_at, color: '#a78bfa',
                        read: !!m.read_at,
                    });
                });
            }

            // 4. Scadenze
            if (isTypeEnabled('deadline')) {
                (data.deadlines || []).forEach(dl => {
                    const due      = new Date(dl.due_date + 'T00:00:00');
                    const daysLeft = Math.round((due - today) / 86400000);
                    const clientName = dl.company_name
                        || `${dl.first_name || ''} ${dl.last_name || ''}`.trim() || '';
                    const isOverdue = daysLeft < 0;
                    addNotif({
                        id:   `deadline-${dl.id}`,
                        type: 'deadline',
                        title: isOverdue
                            ? `🚨 Scadenza non completata (${Math.abs(daysLeft)}gg fa)`
                            : daysLeft === 0 ? '⏰ Scadenza oggi!'
                            : `📅 Scadenza tra ${daysLeft} giorn${daysLeft === 1 ? 'o' : 'i'}`,
                        message: `${dl.title}${clientName ? ` — ${clientName}` : ''}`,
                        time:    dl.due_date + 'T00:00:00.000Z',
                        color:   isOverdue ? '#dc2626' : daysLeft === 0 ? '#ef4444' : daysLeft <= 2 ? '#fb923c' : '#f59e0b',
                        read:    false,
                        link:    dl.client_id ? `/app/clients/${dl.client_id}?tab=notes&sub=scadenze` : '/app/clients',
                    });
                });
            }

            // 5. Monitoring past_due
            if (isTypeEnabled('past_due')) {
                (data.monitoring || []).forEach(s => {
                    const name = s.company_name
                        || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Cliente';
                    addNotif({
                        id:      `monitoring-due-${s.id}`,
                        type:    'past_due',
                        title:   '⚠️ Pagamento in sospeso',
                        message: `${name} — ${s.plan_type === 'business' ? 'Business' : 'Consumer'}`,
                        time:    new Date().toISOString(),
                        color:   '#fb923c',
                        read:    false,
                        link:    s.client_id ? `/app/clients/${s.client_id}?tab=monitoraggio` : '/app/clients',
                    });
                });
            }

            // 6. Richieste pending
            if (isTypeEnabled('request_pending')) {
                (data.requests || []).forEach(r => {
                    const name = r.company_name
                        || `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Cliente';
                    addNotif({
                        id: `request-${r.id}`, type: 'request_pending',
                        title: '📋 Richiesta in attesa',
                        message: `${name} — ${r.request_type || 'Richiesta generica'}`,
                        time: r.created_at, color: '#6366f1', read: true,
                        link: r.client_id ? `/app/clients/${r.client_id}` : '/app/clients',
                    });
                });
            }

            // 7. Nuovi contratti (ultime 24h)
            if (isTypeEnabled('new_contract')) {
                (data.new_contracts || []).forEach(c => {
                    const name = c.company_name
                        || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Cliente';
                    addNotif({
                        id: `contract-new-${c.id}`, type: 'new_contract',
                        title: '📝 Nuovo contratto inserito',
                        message: `${name} — ${c.offer_name || 'Offerta non specificata'}`,
                        time: c.created_at, color: '#34d399', read: true,
                        link: c.client_id ? `/app/clients/${c.client_id}?tab=items` : '/app/clients',
                    });
                });
            }

            console.log('[NotifCtx] RPC OK — sorgenti caricate in 1 roundtrip');
        } catch (e) {
            console.error('[NotifCtx] fetchAllNotifications error:', e);
        }
    }, [authId, orgId, role, addNotif, isTypeEnabled]);



    // ── Bootstrap ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!authId) return;  // solo authId obbligatorio per partire

        // 1. Cache immediata localStorage (zero flicker)
        const cached = loadFromLS(authId);
        if (cached.length > 0) setNotifications(sortNotifs(cached));

        // 2. Preferenze + chiavi lette → poi RPC aggregata (1 roundtrip)
        Promise.all([loadPreferences(), loadReadKeys()]).then(() => {
            fetchAllNotifications();
        });

        // ── Realtime ─────────────────────────────────────────────────
        const channelId = `notif-main-${orgId || 'norg'}-${authId}`;
        const channel = supabase
            .channel(channelId)

            // monitoring_subscriptions — solo se orgId disponibile
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'monitoring_subscriptions',
                  filter: orgId ? `organization_id=eq.${orgId}` : undefined },
                async (payload) => {
                    if (!orgId) return;
                    const row = payload.new || payload.old;
                    if (!row) return;
                    const { data: sub } = await supabase.from('monitoring_subscriptions')
                        .select('plan_type, status, client_id, client:clients(first_name, last_name, company_name)')
                        .eq('id', row.id).single();
                    const name = sub?.client?.company_name || `${sub?.client?.first_name || ''} ${sub?.client?.last_name || ''}`.trim() || 'Cliente';
                    const plan = sub?.plan_type === 'business' ? 'Business' : 'Consumer';
                    const clientId = sub?.client_id || row.client_id || null;
                    const monLink = clientId ? `/app/clients/${clientId}?tab=monitoraggio` : '/app/clients';
                    if (payload.eventType === 'INSERT') {
                        addNotif({ id: `monitoring-new-${row.id}`, type: 'new_sub', title: '\ud83c\udf89 Nuovo monitoraggio', message: `${name} — ${plan}`, time: new Date().toISOString(), color: '#34d399', read: false, link: monLink });
                    } else if (payload.eventType === 'UPDATE') {
                        if (sub?.status === 'active' && payload.old?.status !== 'active') {
                            addNotif({ id: `monitoring-ok-${row.id}`, type: 'payment_ok', title: '✅ Pagamento ricevuto', message: `${name} — ${plan}`, time: new Date().toISOString(), color: '#6366f1', read: false, link: monLink });
                        } else if (['past_due', 'unpaid'].includes(sub?.status)) {
                            addNotif({ id: `monitoring-due-${row.id}-${Date.now()}`, type: 'past_due', title: '⚠️ Pagamento in sospeso', message: `${name} — ${plan}`, time: new Date().toISOString(), color: '#fb923c', read: false, link: monLink });
                        }
                    }
                }
            )
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'broadcasts' },
                (payload) => {
                    const b = payload.new;
                    if (!b.target_role || b.target_role === role) {
                        const TYPE_COLOR = { warning: '#f59e0b', promo: '#a78bfa', info: '#38bdf8' };
                        addNotif({ id: `broadcast-${b.id}`, broadcastId: b.id, type: 'broadcast', title: b.subject, message: b.message, time: b.sent_at || new Date().toISOString(), color: TYPE_COLOR[b.type] || '#38bdf8', read: false });
                    }
                }
            )
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'client_deadlines',
                filter: orgId ? `organization_id=eq.${orgId}` : undefined },
                (payload) => {
                    if (!orgId) return;
                    const dl = payload.new;
                    if (!dl) return;
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const due = new Date(dl.due_date + 'T00:00:00');
                    const daysLeft = Math.round((due - today) / 86400000);
                    if (daysLeft <= 7) {
                        addNotif({ id: `deadline-${dl.id}`, type: 'deadline', title: daysLeft === 0 ? '⏰ Scadenza oggi!' : `📅 Scadenza tra ${daysLeft} giorni`, message: dl.title, time: new Date().toISOString(), color: daysLeft === 0 ? '#ef4444' : '#f59e0b', read: false, link: dl.client_id ? `/app/clients/${dl.client_id}?tab=notes&sub=scadenze` : '/app/clients' });
                    }
                }
            )
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'requests',
                filter: orgId ? `organization_id=eq.${orgId}` : undefined },
                async (payload) => {
                    if (!orgId) return;
                    const r = payload.new;
                    if (!r || (!isAgency && r.agent_id !== userId)) return;
                    const { data: client } = await supabase.from('clients').select('first_name,last_name,company_name').eq('id', r.client_id).single();
                    const name = client?.company_name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'Cliente';
                    addNotif({ id: `request-${r.id}`, type: 'request_pending', title: '📋 Nuova richiesta', message: `${name} — ${r.request_type || 'Richiesta generica'}`, time: new Date().toISOString(), color: '#6366f1', read: false, link: r.client_id ? `/app/clients/${r.client_id}` : '/app/clients' });
                }
            )
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'manager_messages' },
                (payload) => {
                    const m = payload.new;
                    if (!m || (!m.target_all && m.target_user_id !== authId)) return;
                    addNotif({ id: `msg-${m.id}`, type: 'message', title: `💬 ${m.subject || 'Nuovo messaggio'}`, message: (m.message || '').slice(0, 100), time: new Date().toISOString(), color: '#a78bfa', read: false });
                }
            )
            // ── 7. Realtime: notifiche in-app dalla tabella notifications (CTE) ──
            .on('postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${authId}` },
                (payload) => {
                    const n = payload.new;
                    if (!n || !isTypeEnabled(n.type)) return;
                    console.log('[NotifCtx] Realtime CTE ricevuta:', n.type, n.title);
                    addNotif({
                        id:       `inapp-${n.id}`,
                        dbId:     n.id,
                        type:     n.type || 'broadcast',
                        title:    n.title,
                        message:  n.message,
                        time:     n.created_at || new Date().toISOString(),
                        color:    '#22d3ee',
                        read:     false,
                        link:     n.link || '/app/notifications',
                        metadata: n.metadata || {},
                    });
                }
            )
            .subscribe((status) => {
                console.log('[NotifCtx] Realtime channel status:', status);
            });

        channelRef.current = channel;
        return () => {
            supabase.removeChannel(channel);
            channelRef.current = null;
            if (markReadTimer.current) clearTimeout(markReadTimer.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, userId, authId]);

    // ── Salva in localStorage ad ogni update ───────────────────────────────
    useEffect(() => {
        if (authId && notifications.length > 0) {
            saveToLS(authId, notifications);
        }
    }, [notifications, authId]);

    // ── markAllRead ────────────────────────────────────────────────────────
    const markAllRead = useCallback(() => {
        setNotifications(prev => {
            prev.filter(n => !n.read).forEach(n => {
                persistRead(n.id);
                if (n.broadcastId && authId) {
                    supabase.from('broadcast_reads')
                        .insert({ broadcast_id: n.broadcastId, user_id: authId })
                        .then(() => {}).catch(() => {});
                }
            });
            return prev.map(n => ({ ...n, read: true }));
        });
    }, [authId, persistRead]);

    // ── markOneRead ────────────────────────────────────────────────────────
    const markOneRead = useCallback((id) => {
        persistRead(id);
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        // Aggiorna anche notifications.read nel DB per le notifiche in-app
        if (id.startsWith('inapp-')) {
            const dbId = id.replace('inapp-', '');
            supabase.from('notifications').update({ read: true }).eq('id', dbId).then(() => {}).catch(() => {});
        }
    }, [persistRead]);

    // ── dismiss ────────────────────────────────────────────────────────────
    const dismiss = useCallback((id) => {
        setNotifications(prev => {
            const notif = prev.find(n => n.id === id);
            if (notif?.broadcastId && authId) {
                supabase.from('broadcast_reads')
                    .insert({ broadcast_id: notif.broadcastId, user_id: authId })
                    .then(() => {}).catch(() => {});
            }
            persistRead(id);
            return prev.filter(n => n.id !== id);
        });
    }, [authId, persistRead]);

    // ── scheduleMarkAllRead ────────────────────────────────────────────────
    const scheduleMarkAllRead = useCallback((delayMs = 3000) => {
        if (markReadTimer.current) clearTimeout(markReadTimer.current);
        markReadTimer.current = setTimeout(() => {
            markAllRead();
            markReadTimer.current = null;
        }, delayMs);
    }, [markAllRead]);

    const cancelScheduledMarkAllRead = useCallback(() => {
        if (markReadTimer.current) { clearTimeout(markReadTimer.current); markReadTimer.current = null; }
    }, []);

    // ── updatePreferences ─────────────────────────────────────────────────
    const updatePreferences = useCallback(async (updates) => {
        if (!authId) return;
        const merged = { ...preferences, ...updates, updated_at: new Date().toISOString() };
        setPreferences(merged);
        await supabase.from('notification_preferences')
            .upsert({ user_id: authId, ...updates, updated_at: new Date().toISOString() });
    }, [authId, preferences]);

    return (
        <NotifContext.Provider value={{
            notifications, unread, preferences,
            markAllRead, markOneRead, dismiss,
            scheduleMarkAllRead, cancelScheduledMarkAllRead,
            updatePreferences,
        }}>
            {children}
        </NotifContext.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotifContext);
    if (!ctx) throw new Error('useNotifications must be used inside NotificationProvider');
    return ctx;
}
